import React from "react";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Face from "./Face";
import TrackStrip from "./TrackStrip";
import { orderTracks, screenTime, fmt, isUnnamed, isOther } from "./tracksModel";

/**
 * A character on one line: face (with select-all checkbox), name, actor and counts, then its tracks.
 * Renaming happens in place: the name and actor keep their position, an outline appears around them.
 * The row is also a drop target for dragged tracks. The Others row (char_id -1) holds deleted and unassigned
 * tracks: it can't be renamed, and dropping tracks on it deletes them.
 */
function CharacterRow({ c, order, base, sel, dragging, expanded, edit, showMatch, isDrop, menuOpen, h, t }) {
  const tracks = React.useMemo(() => orderTracks(c.tracks_data, order), [c.tracks_data, order]);
  const ids = React.useMemo(() => tracks.map((x) => x.track_id), [tracks]);
  const screen = React.useMemo(() => screenTime(c.tracks_data), [c.tracks_data]);
  const n = tracks.length;
  let on = 0;
  ids.forEach((id) => sel.has(id) && on++);
  const name = c.char_name || "";
  const other = isOther(c);

  return (
    <section
      className={"tk-row" + (isDrop ? " is-drop" : "")}
      aria-label={name}
      onDragOver={(e) => h.dragOver(e, c.char_id)}
      onDragLeave={(e) => h.dragLeave(e, c.char_id)}
      onDrop={(e) => h.drop(e, c.char_id)}
    >
      <div className="tk-ident">
        <div className="tk-avatar">
          <Face base={base} track={other ? null : c.rep_track_id} person />
          <Checkbox
            className={"tk-rowCheck" + (on ? " is-on" : "")}
            size="small"
            disableRipple
            checked={n > 0 && on === n}
            indeterminate={on > 0 && on < n}
            disabled={!n}
            onChange={() => h.toggleAll(ids)}
            inputProps={{ "aria-label": t("Select all tracks of {{name}}", { name }) }}
          />
        </div>
        <div className="tk-identText">
          {edit ? (
            <EditForm c={c} edit={edit} h={h} t={t} />
          ) : (
            <>
              <div className="tk-nameRow">
                <h3 className="tk-name" title={name}>
                  {name}
                </h3>
                {!other && (
                  <IconButton className="tk-editBtn" size="small" title={t("Rename {{name}}", { name })} aria-label={t("Rename {{name}}", { name })} onClick={() => h.startEdit(c.char_id)}>
                    <EditIcon />
                  </IconButton>
                )}
              </div>
              {other ? (
                <div className="tk-actor">{t("Deleted and unassigned tracks")}</div>
              ) : c.char_actor ? (
                <div className="tk-actor">{c.char_actor}</div>
              ) : (
                // the Unnamed badge sits on this line, so the name row keeps its room for the name (Character12…)
                <div className="tk-actorRow">
                  {isUnnamed(name) && <span className="tk-badge">{t("Unnamed")}</span>}
                  <button type="button" className="tk-link tk-addActor" onClick={() => h.startEdit(c.char_id, "actor")}>
                    {t("Add actor")}
                  </button>
                </div>
              )}
              <div className="tk-stat">{n === 1 ? t("1 track") : t("{{count}} tracks", { count: n })}</div>
              <div className="tk-stat">{t("{{time}} on screen", { time: fmt(screen) })}</div>
              {expanded && (
                <button type="button" className="tk-link tk-showLess" onClick={() => h.collapse(c.char_id)}>
                  <ExpandLessIcon />
                  {t("Show less")}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <div className="tk-area">
        {n ? (
          <TrackStrip
            tracks={tracks}
            ids={ids}
            charId={c.char_id}
            charName={name}
            base={base}
            sel={sel}
            dragging={dragging}
            showMatch={showMatch}
            expanded={expanded}
            h={h}
            t={t}
          />
        ) : (
          <div className="tk-empty">
            {other ? t("No deleted or unassigned tracks.") : t("No tracks yet. Drag tracks here, or select tracks and choose Move To.")}
          </div>
        )}
      </div>
      <div className={"tk-rowMenu" + (menuOpen ? " is-open" : "")}>
        <IconButton
          size="small"
          aria-label={t("More actions for {{name}}", { name })}
          title={t("More actions for {{name}}", { name })}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
          onClick={(e) => h.openMenu(e.currentTarget, c.char_id)}
        >
          <MoreVertIcon />
        </IconButton>
      </div>
    </section>
  );
}

/** name and actor edited where they are shown; Enter saves, Esc cancels */
function EditForm({ c, edit, h, t }) {
  return (
    <form
      className="tk-editForm"
      onSubmit={(e) => {
        e.preventDefault();
        h.saveEdit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          h.cancelEdit();
        }
      }}
    >
      <label className="tk-field tk-field--name">
        <span className="tk-fieldLabel">{t("Character")}</span>
        <input value={edit.name} placeholder={c.char_name || ""} autoFocus={edit.focus !== "actor"} onChange={(e) => h.editField("name", e.target.value)} />
      </label>
      <label className="tk-field tk-field--actor">
        <span className="tk-fieldLabel">{t("Actor")}</span>
        <input value={edit.actor} placeholder={t("Actor name")} autoFocus={edit.focus === "actor"} onChange={(e) => h.editField("actor", e.target.value)} />
      </label>
      {(edit.loadingNames || edit.suggestions.length > 0) && (
        <div className="tk-sug">
          <b>{t("Suggested")}</b>
          {edit.loadingNames && <CircularProgress size={12} />}
          {edit.suggestions.map((s) => (
            <button key={s} type="button" className="tk-sugName" onClick={() => h.editField("actor", s)}>
              {s}
            </button>
          ))}
        </div>
      )}
      <div className="tk-editBtns">
        <Button className="Button ButtonXSmall" size="small" onClick={h.cancelEdit}>
          {t("Cancel")}
        </Button>
        <Button type="submit" className="Button ButtonPrimary ButtonXSmall" size="small">
          {t("Save")}
        </Button>
      </div>
    </form>
  );
}

export default React.memo(CharacterRow);
